import {
    Component,
    h,
    Prop,
    Host,
    Element,
    Event,
    EventEmitter,
    State,
    Watch,
} from "@stencil/core";
import { availableLanguages } from "../sf-page-header/api/languageApi";

@Component({
    tag: "ux-iws-sf-language-switch",
    styleUrl: "sf-language-switch.scss",
    shadow: true,
})
export class SfLanguageSwitch {
    @Element() domNode: HTMLUxIwsSfLanguageSwitchElement;
    /** The currently selected language. */
    @Prop() language: string = "";
    /** Whether the language switch is opened. */
    @Prop({ reflect: true }) opened: boolean = false;
    /** id of a target element that should be used for positioning */
    @Prop() targetId: string = "";

    @State() selectedLanguage: string;

    /** is emitted when a language is selected */
    @Event({ bubbles: true, composed: true }) uxChangeLanguage: EventEmitter<{
        language: string;
    }>;

    /** is emitted when the language switch should be closed */
    @Event({ bubbles: true, composed: true }) uxClose: EventEmitter<void>;

    private setLanguage(language: string) {
        this.selectedLanguage = language;
        this.uxChangeLanguage.emit({ language });
    }

    @Watch("language")
    watchLanguage(newValue: string) {
        this.selectedLanguage = newValue || Object.keys(availableLanguages)[0];
    }

    @Watch("opened")
    watchOpened() {
        window.removeEventListener("resize", this.calculatePosition);
        document.body.removeEventListener("mousedown", this.handleBodyClick);
        document.body.removeEventListener("touchstart", this.handleBodyClick);
        if (this.opened) {
            this.calculatePosition();
            this.domNode.querySelector("button")?.focus();
            window.addEventListener("resize", this.calculatePosition);
            document.body.addEventListener("mousedown", this.handleBodyClick);
            document.body.addEventListener("touchstart", this.handleBodyClick);
        }
    }

    // the switch should be centered below the target element.
    // We need to make sure that the switch is not positioned off-screen or cut off
    // The switch must be position:fixed
    private calculatePosition = () => {
        if (!this.targetId) return;
        const targetElement = document.getElementById(this.targetId);
        if (!targetElement) return;

        const { bottom, left } = targetElement.getBoundingClientRect();
        const { innerWidth } = window;
        const marginTop = 5;

        const isOverflowingRight = left + this.domNode.offsetWidth > innerWidth;
        const isOverflowingLeft = left < 0.5 * this.domNode.offsetWidth;
        const centered =
            left + targetElement.offsetWidth / 2 - this.domNode.offsetWidth / 2;
        const overflowingRightPosition = innerWidth - this.domNode.offsetWidth;
        const listLeft = isOverflowingRight
            ? overflowingRightPosition
            : isOverflowingLeft
              ? 0
              : centered;
        this.domNode.style.top = `${bottom + marginTop}px`;
        this.domNode.style.left = `${listLeft}px`;
    };

    disconnectedCallback() {
        window.removeEventListener("resize", this.calculatePosition);
        document.body.removeEventListener("mousedown", this.handleBodyClick);
        document.body.removeEventListener("touchstart", this.handleBodyClick);
    }

    private handleBodyClick = (event: UIEvent): void => {
        if (
            !this.opened ||
            this.domNode === event.target ||
            this.domNode.contains(event.target as Node)
        ) {
            return;
        }
        event.stopPropagation();
        event.preventDefault();
        this.uxClose.emit();
    };

    componentWillLoad() {
        this.selectedLanguage =
            this.language || Object.keys(availableLanguages)[0];
    }

    render() {
        return (
            <Host inert={!this.opened} aria-hidden={!this.opened}>
                <div class="panel">
                    <div class="list">
                        {Object.entries(availableLanguages).map(
                            ([langId, lang]) => (
                                <button
                                    type="button"
                                    class={{
                                        option: true,
                                        "is-selected":
                                            langId === this.selectedLanguage,
                                    }}
                                    key={langId}
                                    onClick={() => this.setLanguage(langId)}
                                >
                                    {lang.label}
                                </button>
                            )
                        )}
                    </div>
                </div>
            </Host>
        );
    }
}
